/**
 * Megamenu: click toggle, category hover/click, close, mobile slide.
 *
 * Mobile approach:
 * - .megamenu uses height animation (0 → auto) for open/close
 * - .megamenu-layout is 200% wide with col2 + col3 side by side
 * - translateX(-50%) slides from col2 to col3
 * - Height of .megamenu adjusts smoothly during column switch
 *
 * @package schnee
 */

const XL_BREAKPOINT = 1200;
const DURATION = 300;

function isMobile() {
    return window.innerWidth < XL_BREAKPOINT;
}

export function megamenuInit() {
    const nav = document.querySelector('.header-main-menu');
    if (!nav) return;

    // ── Open/close megamenu on click ──
    nav.querySelectorAll('.dropdown-toggle').forEach(toggle => {
        const parentLi = toggle.closest('.dropdown');
        const megamenu = parentLi ? parentLi.querySelector('.megamenu') : null;
        if (!megamenu) return;

        toggle.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const isOpen = parentLi.classList.contains('show');
            closeAllMegamenus(nav, parentLi);

            if (!isOpen) {
                parentLi.classList.add('show');
                toggle.setAttribute('aria-expanded', 'true');

                if (isMobile()) {
                    resetMobileView(megamenu);
                    slideOpen(megamenu);
                } else {
                    megamenu.classList.add('show');
                }
            } else {
                if (isMobile()) {
                    slideClose(megamenu, () => {
                        parentLi.classList.remove('show');
                        toggle.setAttribute('aria-expanded', 'false');
                    });
                } else {
                    parentLi.classList.remove('show');
                    megamenu.classList.remove('show');
                    toggle.setAttribute('aria-expanded', 'false');
                }
            }
        });
    });

    // ── Close button ──
    nav.querySelectorAll('.megamenu-close').forEach(btn => {
        btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            closeAllMegamenus(nav);
        });
    });

    // ── Close on click outside ──
    document.addEventListener('click', function (e) {
        if (!nav.contains(e.target)) {
            closeAllMegamenus(nav);
        }
    });

    // ── Close on ESC ──
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            closeAllMegamenus(nav);
        }
    });

    // ── Category links: hover (desktop) + click (mobile) ──
    nav.querySelectorAll('.megamenu-category-link').forEach(link => {
        link.addEventListener('mouseenter', function () {
            if (isMobile()) return;
            activateCategory(this);
        });

        link.addEventListener('click', function (e) {
            if (!isMobile()) return;
            if (!this.classList.contains('has-submenu')) return;

            e.preventDefault();
            e.stopPropagation();

            const megamenu = this.closest('.megamenu');
            if (!megamenu) return;

            activateCategory(this);
            slideToSubitems(megamenu);
        });
    });

    // ── Back button (mobile) ──
    nav.querySelectorAll('.megamenu-back').forEach(btn => {
        btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const megamenu = this.closest('.megamenu');
            if (!megamenu) return;

            slideToCategories(megamenu);
        });
    });
}

/**
 * Activate a category link and its corresponding panel.
 */
function activateCategory(link) {
    const megamenu = link.closest('.megamenu');
    if (!megamenu) return;

    const targetId = link.getAttribute('data-megamenu-target');

    megamenu.querySelectorAll('.megamenu-category-link').forEach(l => l.classList.remove('active'));
    megamenu.querySelectorAll('.megamenu-subitems-panel').forEach(p => p.classList.remove('active'));

    link.classList.add('active');

    const panel = megamenu.querySelector('[data-megamenu-panel="' + targetId + '"]');
    if (panel) panel.classList.add('active');
}

// ════════════════════════════════════
// Mobile: open / close
// ════════════════════════════════════

function slideOpen(el) {
    // Ensure visible at 0 height
    el.classList.add('show');
    el.style.height = '0px';
    el.style.overflow = 'hidden';

    // Measure the visible column (categories) for target height
    const colCat = el.querySelector('.megamenu-col-categories');
    const h = colCat ? colCat.scrollHeight : el.scrollHeight;

    // Animate to target height
    el.style.transition = 'height ' + DURATION + 'ms ease';
    requestAnimationFrame(() => {
        el.style.height = h + 'px';
    });

    // Keep explicit height so it matches the visible column
    setTimeout(() => {
        el.style.overflow = '';
        el.style.transition = '';
    }, DURATION);
}

function slideClose(el, cb) {
    // Height is already set as inline style — just ensure overflow is hidden
    el.style.overflow = 'hidden';

    // Force reflow
    el.offsetHeight; // eslint-disable-line no-unused-expressions

    el.style.transition = 'height ' + DURATION + 'ms ease';
    requestAnimationFrame(() => {
        el.style.height = '0px';
    });

    setTimeout(() => {
        el.classList.remove('show');
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
        resetMobileView(el);
        if (cb) cb();
    }, DURATION);
}

// ════════════════════════════════════
// Mobile: column slide (left ↔ right)
// ════════════════════════════════════

/**
 * Slide from column 2 → column 3 (left).
 * Layout translateX(-50%) shows the right half (col 3).
 * Megamenu height animates to match col 3 content.
 */
function slideToSubitems(megamenu) {
    const layout = megamenu.querySelector('.megamenu-layout');
    const colSub = megamenu.querySelector('.megamenu-col-subitems');
    if (!layout || !colSub) return;

    // Height is already set inline from slideOpen — just ensure overflow
    megamenu.style.overflow = 'hidden';

    // Force reflow
    megamenu.offsetHeight; // eslint-disable-line no-unused-expressions

    megamenu.style.transition = 'height ' + DURATION + 'ms ease';

    // Slide layout
    layout.classList.add('mobile-show-subitems');

    // Calculate target height (col3 content — natural height thanks to align-items: flex-start)
    const toH = colSub.scrollHeight;

    requestAnimationFrame(() => {
        megamenu.style.height = toH + 'px';
    });

    // Keep explicit height to match visible column
    setTimeout(() => {
        megamenu.style.overflow = '';
        megamenu.style.transition = '';
    }, DURATION);
}

/**
 * Slide from column 3 → column 2 (right).
 */
function slideToCategories(megamenu) {
    const layout = megamenu.querySelector('.megamenu-layout');
    const colCat = megamenu.querySelector('.megamenu-col-categories');
    if (!layout || !colCat) return;

    // Lock current height (read from inline style since we keep it explicit)
    megamenu.style.overflow = 'hidden';

    // Force reflow
    megamenu.offsetHeight; // eslint-disable-line no-unused-expressions

    megamenu.style.transition = 'height ' + DURATION + 'ms ease';

    // Slide layout back
    layout.classList.remove('mobile-show-subitems');

    // Calculate target height (col2 content — natural height)
    const toH = colCat.scrollHeight;

    requestAnimationFrame(() => {
        megamenu.style.height = toH + 'px';
    });

    // Keep explicit height to match visible column
    setTimeout(() => {
        megamenu.style.overflow = '';
        megamenu.style.transition = '';
    }, DURATION);
}

// ════════════════════════════════════
// Helpers
// ════════════════════════════════════

function resetMobileView(megamenu) {
    const layout = megamenu.querySelector('.megamenu-layout');
    if (layout) layout.classList.remove('mobile-show-subitems');
}

function closeAllMegamenus(nav, except) {
    nav.querySelectorAll('.dropdown.show').forEach(item => {
        if (except && item === except) return;

        const menu = item.querySelector('.megamenu');
        const toggle = item.querySelector('.dropdown-toggle');

        if (menu && isMobile() && menu.classList.contains('show')) {
            slideClose(menu, () => {
                item.classList.remove('show');
                if (toggle) toggle.setAttribute('aria-expanded', 'false');
            });
        } else {
            item.classList.remove('show');
            if (menu) {
                menu.classList.remove('show');
                menu.style.height = '';
                menu.style.overflow = '';
                menu.style.transition = '';
                resetMobileView(menu);
            }
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
    });
}
